import type { HubEditorial as HubEditorialData } from "@/data/hub-editorial";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";

interface Props {
  data: HubEditorialData;
}

/**
 * Renders real, substantive editorial copy on hub pages so no published page is
 * a thin link-list. Strict H2 → H3 → H4 hierarchy + FAQPage JSON-LD.
 */
export function HubEditorial({ data }: Props) {
  const faqJsonLd = data.faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <section
      aria-label={`${data.label} guide`}
      className="mt-14 rounded-3xl border border-border/60 bg-card/40 p-6 sm:p-10"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
        {data.label} · In-depth
      </p>

      <div className="mt-4 space-y-10">
        {data.sections.map((s) => (
          <div key={s.h2}>
            <h2 className="font-display text-2xl font-bold tracking-tight text-accent sm:text-3xl">
              {s.h2}
            </h2>
            {s.body.map((p) => (
              <p key={p} className="mt-4 text-[15px] leading-7 text-foreground/80">
                {p}
              </p>
            ))}

            {s.subs?.map((sub) => (
              <div key={sub.h3} className="mt-6 border-l-2 border-accent/40 pl-5">
                <h3 className="font-display text-lg font-semibold text-accent/90 sm:text-xl">
                  {sub.h3}
                </h3>
                <p className="mt-2 text-[15px] leading-7 text-foreground/80">{sub.body}</p>

                {sub.details?.map((d) => (
                  <div key={d.h4} className="mt-4">
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      {d.h4}
                    </h4>
                    <p className="mt-1 text-[15px] leading-7 text-foreground/70">{d.body}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}

        {data.faqs.length > 0 && (
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-accent sm:text-3xl">
              Frequently asked questions
            </h2>
            <div className="mt-4 space-y-5">
              {data.faqs.map((f) => (
                <div key={f.q} className="rounded-2xl border border-border/50 bg-background/40 p-5">
                  <h3 className="font-display text-base font-semibold text-foreground sm:text-lg">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-[15px] leading-7 text-foreground/75">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs leading-6 text-foreground/50">
          Figures on this page reflect published 2026 government rates and typical market pricing
          at the time of writing. Rules change — always confirm against the official immigration,
          tax or regulator website for your destination before you act.
        </p>

        <ArticleDisclaimer className="mt-0" />
      </div>


      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
    </section>
  );
}
